import { useEffect, useRef, useState, useMemo } from 'react';
import { Search, ChevronDown, Check, X, Loader2 } from 'lucide-react';

export interface SearchableOption {
  value: string;
  label: string;
  subLabel?: string;
}

interface SearchableSelectProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: (SearchableOption | string)[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  required?: boolean;
  isLoading?: boolean;
}

export function SearchableSelect({
  id,
  value,
  onChange,
  options,
  placeholder = 'Seçiniz...',
  searchPlaceholder = 'Listede ara...',
  emptyMessage = 'Sonuç bulunamadı.',
  disabled = false,
  className = '',
  required = false,
  isLoading = false,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Normalize options to { value, label, subLabel }
  const normalizedOptions = useMemo<SearchableOption[]>(() => {
    return options.map((opt) => {
      if (typeof opt === 'string') {
        return { value: opt, label: opt };
      }
      return opt;
    });
  }, [options]);

  // Current selected option object
  const selectedOption = useMemo(() => {
    return normalizedOptions.find((opt) => opt.value === value) || null;
  }, [normalizedOptions, value]);

  // Filtered options based on search (Turkish case-insensitive support)
  const filteredOptions = useMemo(() => {
    if (!search.trim()) return normalizedOptions;
    const q = search.trim().toLocaleLowerCase('tr-TR');
    return normalizedOptions.filter((opt) => {
      const l = opt.label.toLocaleLowerCase('tr-TR');
      const v = opt.value.toLocaleLowerCase('tr-TR');
      const s = opt.subLabel ? opt.subLabel.toLocaleLowerCase('tr-TR') : '';
      return l.includes(q) || v.includes(q) || s.includes(q);
    });
  }, [normalizedOptions, search]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input on open
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  const handleSelect = (val: string) => {
    onChange(val);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {/* Hidden input for HTML form validation if required */}
      {required && (
        <input
          type="text"
          value={value}
          readOnly
          required
          tabIndex={-1}
          className="sr-only"
          onFocus={() => {
            if (!disabled) setIsOpen(true);
          }}
        />
      )}

      {/* Trigger Button */}
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        className={`field flex w-full items-center justify-between gap-2 text-left transition-colors ${
          disabled ? 'cursor-not-allowed opacity-60 bg-ink-50' : 'cursor-pointer'
        } ${isOpen ? 'ring-2 ring-brand-500 border-brand-500' : ''}`}
      >
        <span className={`block truncate ${!selectedOption ? 'text-ink-400' : 'text-ink-900 font-medium'}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <div className="flex items-center gap-1 shrink-0 text-ink-400">
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-brand-600" />}
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180 text-brand-600' : ''}`}
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-xl border border-ink-200 bg-white shadow-xl py-1.5 text-sm animate-in fade-in-50 zoom-in-95 duration-100">
          {/* Search Box */}
          <div className="px-2.5 pb-2 pt-1 border-b border-ink-100">
            <div className="relative flex items-center">
              <Search className="absolute left-2.5 h-4 w-4 text-ink-400 pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full rounded-lg border border-ink-200 bg-ink-50/60 py-1.5 pl-8 pr-7 text-xs text-ink-900 placeholder:text-ink-400 focus:border-brand-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-500"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setIsOpen(false);
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    if (filteredOptions.length > 0) {
                      handleSelect(filteredOptions[0].value);
                    }
                  }
                }}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 text-ink-400 hover:text-ink-600 p-0.5"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-56 overflow-y-auto px-1 py-1 divide-y divide-ink-50">
            {filteredOptions.length === 0 ? (
              <div className="py-6 px-3 text-center text-xs text-ink-400 font-medium">
                {emptyMessage}
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleSelect(opt.value)}
                    className={`group flex w-full items-center justify-between gap-2 px-3 py-2 text-left rounded-lg text-xs transition-colors ${
                      isSelected
                        ? 'bg-brand-50 text-brand-900 font-semibold'
                        : 'text-ink-800 hover:bg-ink-50'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{opt.label}</div>
                      {opt.subLabel && (
                        <div className="text-[11px] text-ink-400 truncate">{opt.subLabel}</div>
                      )}
                    </div>
                    {isSelected && <Check className="h-4 w-4 text-brand-600 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
